/**
 * Pure view-logic for the reusable Google sync card — no React/DOM, unit-tested
 * (googleSyncView.test.js). Turns the raw /api/google/status payload into the display state
 * the card renders, and turns a POST /api/google/sync response into a human summary line.
 * CommonJS so node/Jest can require it while webpack interop lets the .jsx import it.
 */

// The services this connection can sync, in display order. `key` matches the fields the
// backend returns in the /sync response + status.services[key].
var SERVICES = [
  { key: 'calendar', label: 'Calendar', icon: '📅' },
  { key: 'contacts', label: 'Contacts', icon: '👤' },
  { key: 'tasks', label: 'Tasks', icon: '✓' },
];

// status: { connected, email, lastSync, lastFreeBusyCheck, health, lastError, services? }
function deriveGoogleView(status) {
  status = status || {};
  var connected = !!status.connected;
  var health = status.health || (connected ? 'ok' : 'local_only');
  var needsAttention = health === 'degraded' || health === 'needs_reauth' || !!status.lastError;
  var title = !connected ? 'Not connected to Google'
    : health === 'needs_reauth' ? 'Google needs re-authorization'
    : health === 'degraded' ? 'Google sync degraded'
    : 'Google connected';
  return {
    connected: connected,
    health: health,
    needsAttention: needsAttention,
    title: title,
    email: status.email || null,
    lastSync: status.lastSync || null,
    lastError: status.lastError || null,
  };
}

// Which services to show, given an optional whitelist (a page may want only its own).
function visibleServices(only) {
  if (!only || !only.length) return SERVICES.slice();
  return SERVICES.filter(function (s) { return only.indexOf(s.key) !== -1; });
}

// Per-service one-liner from a service result block { imported, updated, skipped, pushed, error }.
function serviceResultText(res) {
  if (!res) return null;
  if (res.error) return 'error: ' + res.error;
  var parts = [];
  if (res.imported != null) parts.push(res.imported + ' imported');
  if (res.updated != null) parts.push(res.updated + ' updated');
  if (res.pushed != null) parts.push(res.pushed + ' pushed');
  if (res.skipped != null) parts.push(res.skipped + ' skipped');
  return parts.length ? parts.join(', ') : 'up to date';
}

// Whole-sync summary from the POST /api/google/sync response.
function syncSummary(data) {
  data = data || {};
  if (data.needsReconnect) {
    return { type: 'error', text: data.message || 'Google needs reconnection — click Re-authorize.' };
  }
  var anyError = false;
  var bits = SERVICES.map(function (s) {
    var r = data[s.key];
    if (!r) return null;
    if (r.error) anyError = true;
    return s.label + ': ' + serviceResultText(r);
  }).filter(Boolean);
  if (!bits.length) return { type: 'success', text: 'Sync complete.' };
  return { type: anyError ? 'error' : 'success', text: bits.join(' · ') };
}

module.exports = {
  SERVICES: SERVICES,
  deriveGoogleView: deriveGoogleView,
  visibleServices: visibleServices,
  serviceResultText: serviceResultText,
  syncSummary: syncSummary,
};
